<template>
  <div>
    <v-card id="commission-receipt-list">
      <v-card-text class="row pb-0 pr-0">
        <div class="col-sm-3 col-12 pb-sm-8 pb-1 pl-2 pr-1 pt-sm-0">
          <!-- Date range -->
          <v-menu
            v-model="isDateMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :append-icon="icons.mdiCalendar"
                :value="dateRangeText"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateRange"
              color="primary"
              :first-day-of-week="1"
              range
            >
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                block
                @click="dateRangeChange()"
              >
                {{ t('Set Dates') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </div>

        <div class="col-sm-3 offset-sm-3 col-12 pb-sm-8 pb-1 pl-2 pr-1 pt-sm-0">
          <!-- filter account -->
          <v-combobox
            v-model="accountFilters"
            :items="accounts"
            item-text="name"
            single-line
            multiple
            outlined
            dense
            hide-details
            clearable
            :placeholder="t('Select Account')"
            :class="{ 'receipt-list-account': $vuetify.breakpoint.smAndUp }"
          ></v-combobox>
        </div>

        <div class="col-sm-3 col-12 pb-sm-8 pb-1 pl-2 pr-1 pt-sm-0">
          <v-btn
            block
            color="primary"
            @click="fetchCommission()"
          >
            <v-icon
              class="pr-2"
              size="30"
            >
              {{ icons.mdiMagnify }}
            </v-icon>
            Search
          </v-btn>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text class="pt-5">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <div class="d-flex align-center">
              <v-select
                v-model="tablePagination.per"
                :items="listLengthOptions"
                single-line
                outlined
                dense
                hide-details
                class="receipt-list-row-selection"
                @input="listLengthChange()"
              ></v-select>
              <div class="pl-3">
                {{ t('Rows') }}
              </div>
            </div>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="tablePagination.current_page"
              total-visible="6"
              :length="tablePagination.total_page"
              @input="fetchCommission()"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- table -->
      <v-data-table
        :headers="tableColumns"
        :items="receipts"
        :items-per-page="100"
        :loading="loading"
        mobile-breakpoint="0"
        hide-default-footer
        :disable-sort="true"
        class="text-no-wrap"
      >
        <!-- Actions -->
        <template #[`item.actions`]="{item}">
          <div class="d-flex align-center">
            <!-- Edit -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  :to="{
                    name: 'apps-receipt-edit',
                    params: { id: item.id }
                  }"
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="18">
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Receipt Number -->
        <template #[`item.receipt_number`]="{item}">
          <router-link
            class="font-weight-medium text-decoration-none"
            :to="{ name: 'apps-receipt-preview', params: { id: item.id } }"
          >
            {{ item.receipt_number }}
          </router-link>
        </template>
      </v-data-table>

      <v-card-text class="pt-5">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <div class="d-flex align-center">
              <v-select
                v-model="tablePagination.per"
                :items="listLengthOptions"
                single-line
                outlined
                dense
                hide-details
                class="receipt-list-row-selection"
                @input="listLengthChange()"
              ></v-select>
              <div class="pl-3">
                {{ t('Rows') }}
              </div>
            </div>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="tablePagination.current_page"
              total-visible="6"
              :length="tablePagination.total_page"
              @input="fetchCommission()"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <div class="text-right">
      <v-btn
        color="primary"
        :loading="loadingPrint"
        :disabled="loadingPrint"
        :block="$vuetify.breakpoint.xsOnly"
        class="receipt-button mt-5"
        @click="printCommissionReport()"
      >
        <v-icon
          v-if="!loadingPrint"
          size="18"
          class="me-2"
        >
          {{ icons.mdiPrinter }}
        </v-icon>
        <span>{{ t('Print Report') }}</span>
      </v-btn>
    </div>

    <v-card class="my-5">
      <v-card-title class="align-start">
        <span :class="{ 'text-sm': $vuetify.breakpoint.xsOnly }">{{ t('Summary for') }} {{ dateRangeText }}</span>
      </v-card-title>

      <v-card-text>
        <v-row class="py-3">
          <v-col
            cols="12"
            sm="6"
            lg="4"
            class="px-5 py-0"
            :class="{
              'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
              'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
            }"
          >
            <v-card-title
              class="align-start d-flex align-center pl-0"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Total Policies Issued
              </p>
            </v-card-title>

            <PoliciesIssuedBreakdown
              v-for="customerType in ['Quoted', 'Non quoted/Broker', 'Corporate', 'Niagamas loyalty', 'Staff rate', 'Niagamas merchant', '']"
              :key="customerType"
              :customer-type="customerType"
              :summary="receiptsSummary.total_policies_issued"
              class="font-weight-semibold"
            />

            <v-card-title
              class="align-start d-flex align-center px-0"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Total Commission
              </p>
              <v-spacer></v-spacer>
              <span>
                {{ receiptsSummary.total_commission }}
              </span>
            </v-card-title>
          </v-col>
          <v-col
            v-for="(policies, user) in receiptsSummary.policies_issued"
            :key="user"
            cols="12"
            sm="6"
            lg="4"
            class="px-5 py-0"
            :class="{
              'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
              'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
            }"
          >
            <v-card-title
              class="align-start d-flex align-center pl-0"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Policies Issued by {{ user }}
              </p>
            </v-card-title>

            <PoliciesIssuedBreakdown
              v-for="customerType in ['Quoted', 'Non quoted/Broker', 'Corporate', 'Niagamas loyalty', 'Staff rate', 'Niagamas merchant', '']"
              :key="customerType"
              :customer-type="customerType"
              :summary="policies"
              class="font-weight-semibold"
            />

            <v-card-title
              class="align-start d-flex align-center px-0"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Commission
              </p>
              <v-spacer></v-spacer>
              <span>
                {{ receiptsSummary.commission[user] }}
              </span>
            </v-card-title>
            <v-card-title
              class="align-start d-flex align-center px-0 pt-0 pb-1"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Total After Discount
              </p>
              <v-spacer></v-spacer>
              <span>
                {{ receiptsSummary.after_discount[user] }}
              </span>
            </v-card-title>
            <v-card-title
              class="align-start d-flex align-center px-0"
              style="font-size: 16px;"
            >
              <p class="d-flex align-center mb-0">
                Total Net Amount
              </p>
              <v-spacer></v-spacer>
              <span>
                {{ receiptsSummary.net_amount[user] }}
              </span>
            </v-card-title>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  mdiCurrencyUsd,
  mdiAlertOutline,
  mdiCalendar,
  mdiClose,
  mdiMagnify,
  mdiPencilOutline,
  mdiPrinter,
} from '@mdi/js'
import {
  ref,
  inject,
  computed,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import PoliciesIssuedBreakdown from '@/components/apps/summaries/PoliciesIssuedBreakdown.vue'
import receiptStoreModule from '../receipt/receiptStoreModule'

export default {
  components: {
    PoliciesIssuedBreakdown,
  },
  setup() {
    // Initializers
    const RECEIPT_APP_STORE_MODULE_NAME = 'app-receipt'
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const format = 'csv'

    if (!store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) {
      store.registerModule(RECEIPT_APP_STORE_MODULE_NAME, receiptStoreModule)
    }

    // Table Handlers
    const tableColumns = computed(() => (
      [
        { text: t('Actions'), value: 'actions' },
        { text: t('Issuer'), value: 'issuer' },
        { text: t('#Receipt No.'), value: 'receipt_number' },
        { text: t('Issue Date'), value: 'issued_at' },
        { text: t('Commission Earned'), value: 'commission_amount' },
        { text: t('Claimed At'), value: 'commission_claimed' },
      ]
    ))

    // Properties
    const [dayNow, monthNow, yearNow] = new Date().toLocaleDateString('en-SG').split('/')
    const receipts = ref([])
    const receiptsSummary = ref([])
    const dateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const isDateMenuOpen = ref(false)
    const loading = ref(false)
    const loadingPrint = ref(false)
    const isClaimDialogVisible = ref(false)
    const accounts = ref([])
    const accountFilters = ref([])
    const listLengthOptions = ref([20, 50, 100])
    const tablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const commission = ref({
      unclaimed: null,
      claimed: null,
    })

    // Utils
    const formatDate = date => {
      if (!date) return null
      const [year, month, day] = date.split('-')

      return `${day}/${month}/${year}`
    }

    // Computed
    const dateRangeText = computed(() => {
      if (Date.parse(dateRange.value[0]) > Date.parse(dateRange.value[1])) {
        const [start, end] = dateRange.value
        dateRange.value[0] = end
        dateRange.value[1] = start
      }

      const startDateText = formatDate(dateRange.value[0])
      const showEndDate = dateRange.value[0] !== dateRange.value[1] && formatDate(dateRange.value[1]) !== null
      const endDateText = showEndDate ? ` - ${formatDate(dateRange.value[1])}` : ''

      return `${startDateText}${endDateText}`
    })

    // Methods
    const isLoading = () => {
      loading.value = true
      snackbarService.info('Loading your list. Please hold...')
    }
    const doneLoading = () => {
      loading.value = false
      snackbarService.success('Done loading your list!')
    }
    const fetchClerks = () => {
      store
        .dispatch('app-receipt/fetchClerks', { role: [1, 4, 6] })
        .then(response => {
          const { data } = response.data
          accounts.value = data
        })
        .catch(error => snackbarService.error(error.response.data.message || 'Something went wrong while fetching accounts. Please refresh!'))
    }
    const fetchCommission = () => {
      isLoading()
      store
        .dispatch('fetchCommissionStatistics', {
          page: tablePagination.value.current_page,
          per: tablePagination.value.per,
          user_ids: accountFilters.value.map(account => account.id),
          start_date: dateRange.value[0],
          end_date: dateRange.value[1] ? dateRange.value[1] : dateRange.value[0],
        })
        .then(response => {
          const { data, pagination, summary } = response.data
          receipts.value = data
          receipts.value.forEach(field => {
            Object.keys(field).forEach(key => {
              if (field[key] === null) field[key] = 'NULL'
            })
          })
          receiptsSummary.value = summary
          tablePagination.value = pagination
          commission.value = response.data.commission
          doneLoading()
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching commission. Please refresh!')
        })
    }
    const printCommissionReport = () => {
      loadingPrint.value = true
      store
        .dispatch('printCommissionStatisticsReport', {
          user_ids: accountFilters.value.map(account => account.id),
          start_date: dateRange.value[0],
          end_date: dateRange.value[1] ? dateRange.value[1] : dateRange.value[0],
          format,
        })
        .then(response => {
          const url = window.URL.createObjectURL(response)
          const a = document.createElement('a')
          a.href = url
          a.download = `commission(${dateRangeText.value}).${format}`
          document.body.appendChild(a)
          a.click()
          a.remove()
          loadingPrint.value = false
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while printing report. Please refresh!')
        })
    }
    const dateRangeChange = () => {
      isDateMenuOpen.value = false
      fetchCommission()
    }
    const listLengthChange = () => {
      tablePagination.value.current_page = 1
      fetchCommission()
    }

    // Mounted
    onMounted(() => {
      fetchClerks()
      fetchCommission()
    })

    // Unmounted
    onUnmounted(() => {
      if (store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) store.unregisterModule(RECEIPT_APP_STORE_MODULE_NAME)
    })

    return {
      t,
      loading,
      loadingPrint,
      isClaimDialogVisible,
      tableColumns,
      tablePagination,
      listLengthOptions,
      listLengthChange,
      fetchClerks,
      fetchCommission,
      printCommissionReport,
      commission,
      receipts,
      receiptsSummary,
      accounts,
      accountFilters,
      dateRange,
      dateRangeText,
      dateRangeChange,
      formatDate,
      isDateMenuOpen,
      icons: {
        mdiCurrencyUsd,
        mdiAlertOutline,
        mdiCalendar,
        mdiClose,
        mdiMagnify,
        mdiPencilOutline,
        mdiPrinter,
      },
      components: {
        PoliciesIssuedBreakdown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
  #commission-receipt-list {
    .receipt-list-row-selection {
      max-width: 5.3rem;
    }
  }

  .receipt-button {
    width: 251px;
  }

  .dark-divider {
    border-right: 1px solid rgba(231, 227, 252, 0.14) !important;
  }

  .light-divider {
    border-right: 1px solid rgba(94, 86, 105, 0.14);
  }
</style>
